/** Small shared hooks. Nothing here knows about a domain. */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { mediaQuery } from './media-query';

/**
 * A value that settles only after `delay` of quiet.
 *
 * What makes a search box one request per phrase instead of one per keystroke.
 * The delay is the caller's because it is a judgement: 250ms feels instant for
 * a search, and would be too eager for something expensive.
 */
export function useDebounced<T>(value: T, delay = 250): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return settled;
}

/**
 * State kept in sync with `localStorage`, for a per-viewer convenience.
 *
 * For a remembered tab, a collapsed sidebar, a table layout. Never for
 * anything the product needs to be correct — see `persist.ts`.
 */
export function usePersistentState<T>(
  key: string,
  initial: T,
): [T, (value: T | ((previous: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });

  const update = useCallback(
    (value: T | ((previous: T) => T)) => {
      setState((previous) => {
        const next = typeof value === 'function' ? (value as (p: T) => T)(previous) : value;
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // A convenience that could not be saved is still a working session.
        }
        return next;
      });
    },
    [key],
  );

  return [state, update];
}

/** The smallest height a filled element is given, however little room is left. */
const MIN_FILL_HEIGHT = 240;

/**
 * Give an element the height left for it in the page, so the page is exactly
 * the screen: the header above, the footer below, and the element's own rows
 * scrolling inside it.
 *
 * A register should fill the view with its own internal scroll, so the toolbar
 * and the pager stay put while the rows move. `100vh` minus a guessed header
 * height is wrong the moment anything wraps, so this measures.
 *
 * **It measures against the page's scroll area, never the window.** The shell
 * scrolls in one `ScrollArea fill` (the document never scrolls) and ends with
 * a footer inside it; sizing to `window.innerHeight - top` gave the element
 * the footer's room too, so every register page ran past the screen. The room
 * is: the area's height, less everything above the element, whatever the page
 * puts after it, the page's bottom padding, and whatever follows the page
 * (the footer). An element whose nearest scroll area is not the page's own —
 * inside a drawer, a dialog, a card that scrolls — is left to its content.
 *
 * **What the page puts after it is what is drawn there** — the element's
 * following siblings, at every level up to the page — never the distance to
 * the page's own bottom. A page that fills the view grows to the foot of the
 * screen (`flex-1`, the shells' rule), so its bottom is not where its content
 * ends: counting that stretch as content gave every such register the minimum
 * height (the email setup and incoming-email registers, 2026-10-19).
 *
 * Re-measures when the area or the page resizes, and when anything drawn beside
 * the element — above it or after it, at any level up to the page — resizes,
 * appears or goes: a filter chip row wrapping to a second line, a status line
 * replacing its loading placeholder, an alert arriving.
 */
export function useFillHeight(): {
  ref: React.RefObject<HTMLDivElement | null>;
  height: number | undefined;
} {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const element = ref.current;
    const area = element?.parentElement?.closest<HTMLElement>('[data-simplebar]');
    if (!element || !area?.classList.contains('scroll-area-fill') || typeof ResizeObserver === 'undefined') {
      setHeight(undefined);
      return undefined;
    }
    const content = area.querySelector<HTMLElement>('.simplebar-content');
    const main = content ? [...content.children].find((child) => child.contains(element)) : undefined;
    const page = main ? [...main.children].find((child) => child.contains(element)) : undefined;
    if (!content || !(main instanceof HTMLElement) || !(page instanceof HTMLElement)) return undefined;

    const measure = () => {
      const box = element.getBoundingClientRect();
      const top = content.getBoundingClientRect().top;
      const above = box.top - top;
      let lowest = box.bottom;
      for (let node: Element | null = element; node && node !== page; node = node.parentElement) {
        for (let next = node.nextElementSibling; next; next = next.nextElementSibling) {
          const drawn = next.getBoundingClientRect();
          if (drawn.height > 0) lowest = Math.max(lowest, drawn.bottom);
        }
      }
      const afterInPage = lowest - box.bottom + (parseFloat(getComputedStyle(page).paddingBottom) || 0);
      const padding = parseFloat(getComputedStyle(main).paddingBottom) || 0;
      const afterPage = content.getBoundingClientRect().bottom - main.getBoundingClientRect().bottom;
      setHeight(Math.max(MIN_FILL_HEIGHT, Math.floor(area.clientHeight - above - afterInPage - padding - afterPage)));
    };

    // Whatever is drawn beside it at any level up to the page moves it or what
    // follows it: a status line above that loads shorter than its placeholder,
    // an alert that appears. The page itself may not change size at all — a
    // page that fills the view (`flex-1`) is the screen's height whatever is
    // in it — so watching only the page missed every one of them.
    const resizes = new ResizeObserver(measure);
    const watch = () => {
      resizes.disconnect();
      resizes.observe(area);
      resizes.observe(page);
      for (let node: Element | null = element; node && node !== page; node = node.parentElement) {
        for (const sibling of node.parentElement?.children ?? []) {
          if (sibling !== node) resizes.observe(sibling);
        }
      }
    };
    // A sibling that comes or goes is a new thing to watch, and moves it too.
    const arrivals = new MutationObserver(() => {
      watch();
      measure();
    });
    for (let node: Element | null = element.parentElement; node; node = node.parentElement) {
      arrivals.observe(node, { childList: true });
      if (node === page) break;
    }
    watch();
    measure();
    return () => {
      resizes.disconnect();
      arrivals.disconnect();
    };
  }, []);

  return { ref, height };
}

/** Whether a media query currently matches. Re-renders when it changes. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => mediaQuery(query)?.matches ?? false);

  useEffect(() => {
    const list = mediaQuery(query);
    // Without `matchMedia` the answer is whatever the initial state said and
    // cannot change, so there is nothing to subscribe to.
    if (!list) return;
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    // Set immediately as well: the query may already have changed between the
    // initial state and this effect running.
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** True below the `md` breakpoint. The one place that number is written down. */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)');
}

/** Run a handler when a click lands outside the element, and on Escape. */
export function useDismiss(
  ref: React.RefObject<HTMLElement | null>,
  onDismiss: () => void,
  active = true,
): void {
  useEffect(() => {
    if (!active) return;

    const onPointerDown = (event: PointerEvent) => {
      const element = ref.current;
      if (element && !element.contains(event.target as Node)) onDismiss();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };

    // `pointerdown` rather than `click`: a menu that closes on click-up stays
    // open through a drag that started outside it, which reads as stuck.
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [ref, onDismiss, active]);
}

/**
 * The latest value, without making a callback depend on it.
 *
 * For an effect that must read current state but must not re-run when it
 * changes — the alternative is an exhaustive-deps suppression, which silences
 * the one lint rule that catches real stale-closure bugs.
 */
export function useLatest<T>(value: T): React.RefObject<T> {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

/**
 * A dialog's scroll region bounded by the dialog's own `max-height`.
 *
 * A dialog is a column — title, a `ScrollArea` body (`min-h-0 flex-1`),
 * footer — that grows with its content up to `max-h-[90vh]`. A `max-height`
 * gives the body no definite height, so SimpleBar's `height: 100%` on its
 * wrapper (tokens.css) resolves to `auto`: the wrapper grew to the whole
 * content, never scrolled, and — the body being `position: relative` — was
 * painted over the footer and took its clicks, so a long form's Save could not
 * be pressed on a short screen. Here the body gets a definite `max-height`
 * — the dialog's, less its title and footer, measured as they change —
 * which is the case SimpleBar sizes itself for.
 *
 * Sets `--dialog-chrome` on the panel; the body's class reads it.
 */
export function useDialogBody(panelRef: React.RefObject<HTMLElement | null>, open: boolean): void {
  // A dialog renders into a portal that mounts a frame after it opens: until
  // the panel is there, look again on the next frame.
  const [frame, setFrame] = useState(0);
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!open) return undefined;
    if (!panel) {
      const next = requestAnimationFrame(() => setFrame((n) => n + 1));
      return () => cancelAnimationFrame(next);
    }
    const measure = () => {
      let chrome = 0;
      for (const child of Array.from(panel.children)) {
        if (!(child as HTMLElement).hasAttribute('data-simplebar')) {
          chrome += (child as HTMLElement).offsetHeight;
        }
      }
      panel.style.setProperty('--dialog-chrome', `${chrome}px`);
    };
    measure();
    // jsdom has no ResizeObserver: a test lays out with the first measure.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    for (const child of Array.from(panel.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [panelRef, open, frame]);
}

/** The body class of a dialog using {@link useDialogBody}. */
export const DIALOG_BODY = 'min-h-0 flex-1 max-h-[calc(90vh-var(--dialog-chrome,0px))]';
